import {TYPE} from "../../../shared/types/types";

//Helper method to check if the days are the same
export function isSameDay(d1: Date, d2: Date): boolean {
  return d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear();
}

//Helper method to get the start of the week based the
export function getStartOfWeek(date: Date, startDayIndex: number): Date {
  const d = new Date(date);
  const currentDay = d.getDay();

  const diff = d.getDate() - ((currentDay - startDayIndex + 7) % 7);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

//Helper method to get the end of the week based on the start day index
export function getEndOfWeek(date: Date, startDayIndex: number): Date {
  const d = new Date(date);
  const currentDay = d.getDay();

  const diff = d.getDate() - ((currentDay - startDayIndex + 7) % 7) + 6;
  d.setDate(diff);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function calendarTimeStepMinutes(): number {
  return 30;
}

export function getEndDateTimeOneHourLater(startTime?: Date): { start: Date; end: Date } {
    const now = startTime ?? new Date();
    const step = calendarTimeStepMinutes();
    const start = roundToNextStep(now, step);
    const end = new Date(start.getTime() + step * 60 * 1000); // +1 step
    return {start, end};
}

function roundToNextStep(date: Date, stepMinutes: number): Date {
    const ms = 1000 * 60 * stepMinutes;
    return new Date(Math.ceil(date.getTime() / ms) * ms);
}

export function decodeRrule(rrule: string): TYPE.RecurrenceRule {
  if(!rrule || rrule.trim() === '') {
    return {repeatType: 'never'};
  }

  const parts = rrule.split(';');
  const mappedRule: TYPE.RecurrenceRule = {
    repeatType: 'never',
    repeatEvery: 1,
    repeatOn: [],
    monthlyRepeatMode: false,
    repeatDaysCount: null,
    repeatDaysUntil: null,
  };

  for (const part of parts) {
    const [key, value] = part.split('=');
    switch (key) {
      case 'FREQ':
        mappedRule.repeatType = value.toLowerCase() as TYPE.RecurrenceRule['repeatType'];
        break;
      case 'INTERVAL':
        mappedRule.repeatEvery = parseInt(value, 10);
        break;
      case 'COUNT':
        mappedRule.repeatEndType = 'count';
        mappedRule.repeatDaysCount = parseInt(value, 10);
        break;
      case 'UNTIL':
        mappedRule.repeatEndType = 'until';
        mappedRule.repeatDaysUntil = parseUntilDate(value);
        break;
      case 'BYDAY':
        if (value.includes(',')) {
          mappedRule.repeatOn = value.split(',');
        } else {
          // Check if value has a numeric prefix (e.g., "1MO", "-1FR")
          // If it does, it's for monthly recurrence; otherwise, it's a simple weekday
          if (/^-?\d/.test(value)) {
            mappedRule.monthlyWeekday = value as TYPE.RecurrenceRule['monthlyWeekday'];
          } else {
            mappedRule.repeatOn = [value];
          }
        }
        break;
      case 'BYMONTHDAY':
        mappedRule.monthlyRepeatMode = true;
        mappedRule.monthlyRepeatDay = parseInt(value, 10);
        break;
      case 'BYSETPOS':
        mappedRule.monthlyRepeatMode = false;
        mappedRule.monthlyOrdinal = value as TYPE.RecurrenceRule['monthlyOrdinal'];
        break;
      case 'BYMONTH':
        mappedRule.repeatMonth = parseInt(value, 10);
        break;
    }
  }
  return mappedRule;
}

function parseUntilDate(value: string): Date {
  const year = +value.substring(0, 4);
  const month = +value.substring(4, 6) - 1;
  const day = +value.substring(6, 8);
  const hour = +value.substring(9, 11);
  const min = +value.substring(11, 13);
  const sec = +value.substring(13, 15);
  return new Date(Date.UTC(year, month, day, hour, min, sec));
}

export function getPreviousMonthStartDate(referenceDate: Date): Date {
  const previousMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - 1, 1);
  previousMonth.setHours(0, 0, 0, 0);
  return previousMonth;
}

export function getNextMonthEndDate(referenceDate: Date): Date {
  const nextMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 2, 0);
  nextMonth.setHours(23, 59, 59, 999);
  return nextMonth;
}

export function getYearStartDate(referenceDate: Date): Date {
  const year = new Date(referenceDate.getFullYear(), 0, 1);
  year.setHours(0, 0, 0, 0);
  return year;
}

export function getYearEndDate(referenceDate: Date): Date {
  const year = new Date(referenceDate.getFullYear(), 11, 31);
  year.setHours(23, 59, 59, 999);
  return year;
}

export function getMonthsBetweenDates(start: Date, end: Date): string[] {
  const months: string[] = [];
  const beginningDate = new Date(start.getFullYear(), start.getMonth(), 1);
  const lastDate = new Date(end.getFullYear(), end.getMonth(), 1);

  while (beginningDate <= lastDate) {
    const y = beginningDate.getFullYear();
    const m = beginningDate.getMonth() + 1;
    months.push(`${y}-${m < 10 ? '0' + m : m}`);
    beginningDate.setMonth(beginningDate.getMonth() + 1);
  }
  return months;
}

export function endTimeCalculation(startAt: Date, duration: number, isFullDay: boolean): Date {
  if(isFullDay) {
    const allDaySpan = Math.floor(duration / 1440);
    const endDate = new Date(startAt);
    endDate.setDate(endDate.getDate() + (allDaySpan - 1));
    endDate.setHours(23, 59, 59, 0);
    return endDate;
  } else {
    return new Date(new Date(startAt).getTime() + duration * 60000);
  }
}

export function formatTimeIgnoringOffset(date: Date): string | null {
  if(date === null || date === undefined) {
    return null;
  }
  const hh = date.getHours().toString().padStart(2, '0');
  const mm = date.getMinutes().toString().padStart(2, '0');
  
  return `${hh}:${mm}`;
}

export function formatTimeAndMinuteToDate(date: Date, time: string): Date {
  const [hours, minutes] = time.split(':').map(Number);
  const newDate = new Date(date);
  newDate.setHours(hours, minutes, 0, 0);
  return newDate;
}
